import moment from "moment";
import { get } from "lodash";

import { Enums } from "../../thor.enums";

export class WordpressService {
    public static $inject: string[] = [
        "$http",
        "$httpParamSerializer",
        "$log",
        "$q",
        "configService",
        "ieUrlBuilderService",
        "localeNumber",
        "sitesService"
    ];

    constructor(
        private $http: ng.IHttpService,
        private $httpParamSerializer: ng.IHttpParamSerializer,
        private $log: ng.ILogService,
        private $q: ng.IQService,
        private configService,
        private ieUrlBuilderService,
        private localeNumber,
        private sitesService
    ) {}

    public logIn(username, password) {
        return this._logIn(username, password)
            .then(() => {
                localStorage.setItem("wpUsername", username);
                localStorage.setItem("wpPassword", password);
            });
    }

    public sendItem(item, itemMedia: Blob, galleryName: string) {
        const mediaUrl = item.medias[0].url;
        let mediaFilename = mediaUrl.substring(mediaUrl.lastIndexOf("/") + 1);
        if (!mediaFilename.match(/.(jpg|jpeg)$/i)) {
            this.$log.info("Send item with no extension media", mediaFilename, itemMedia);
            if (itemMedia.type === "image/jpeg") {
                mediaFilename += ".jpeg";
            } else {
                this.$log.error("Unhandle blob type when sending item media with no extension to wordpress");
            }
        }
        const ieItemUrl = this.ieUrlBuilderService.buildItemUrl(item, galleryName);
        const formData = new FormData();

        formData.append("title", `${ galleryName } - ${ item.description }`);
        formData.append("file", itemMedia, mediaFilename);

        let descriptionData = `Par ${item.organization?.address?.name || ""} à ${
            item.sale.address?.city || "--"
        } le ${moment(item.sale.datetime).format("L")} : ${item.description}`;

        if (!item.isItemPast) {
            descriptionData += ` <a href="${ieItemUrl}" target="_blank">Voir le lot</a>`;
        }

        formData.append("description", descriptionData);

        const auctionedOrEstimated = get(item, "pricing.auctioned") ? "Adjugé" : "Estimé";
        const currency = get(item, "sale.currency", Enums.Currency.EUR);
        let price: string = "";

        if (auctionedOrEstimated === "Adjugé") {
            price = get(item, "pricing.auctioned.price", "Invendu");

            if (price !== "Invendu") {
                price = this.localeNumber.formatCurrency(price, currency);
            }
        } else {
            const min = get(item, "pricing.estimates.min", "0");
            let max = get(item, "pricing.estimates.max", "NC");

            if (max !== "NC") {
                max = this.localeNumber.formatCurrency(max, currency);
            }

            price = `${this.localeNumber.formatCurrency(min, currency)} - ${max}`;
        }

        formData.append("caption", auctionedOrEstimated + " " + price);

        return this._getToken()
            .then(token => {
                const deferred = this.$q.defer();
                const request = new XMLHttpRequest();

                request.open("POST", `${ this.configService.lemag.apiUrl }/media`);
                request.setRequestHeader("Authorization", `Bearer ${ token }`);
                request.setRequestHeader("Content-Disposition", `attachment; filename='${ mediaFilename }'`);
                request.send(formData);

                request.onreadystatechange = () => {
                    /**
                     * ReadyState 4 = Done
                     * https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/readyState
                     */
                    if (request.readyState === 4) {
                        switch (request.status) {
                            case 201:
                                deferred.resolve();
                                break;
                            case 403:
                                localStorage.removeItem("wpToken");
                                this.sendItem(item, itemMedia, galleryName)
                                    .then(() => deferred.resolve())
                                    .catch(() => deferred.reject());
                                break;
                            default:
                                deferred.reject();
                        }
                    }
                };

                return deferred.promise;
            });
    }

    public sendSale(sale) {
        return this._getToken()
            .then(token => {
                const ieSaleUrl = this.ieUrlBuilderService.buildSaleUrl(sale);
                const formattedSaleDate = moment(sale.datetime).format("L");
                const isoSaleDate = moment(sale.datetime).toISOString();

                return this.$http.post(
                    `${ this.configService.lemag.apiUrl }/vente`,
                    this.$httpParamSerializer({
                        title: `${ formattedSaleDate }_${ sale.name }`,
                        status: "publish",
                        "fields[date_de_la_vente]": isoSaleDate,
                        "fields[adresse_du_lieu]": sale.address.formatted_address,
                        "fields[description_de_la_vente]": sale.description,
                        "fields[lien_de_la_vente]": ieSaleUrl,
                        "fields[type_de_la_vente]": this.sitesService.isSiteLive(sale.sites) ?
                            Enums.AuctionType.Live :
                            this.sitesService.isOnline(sale.sites) ?
                                Enums.AuctionType.Online :
                                Enums.AuctionType.Classic
                    }),
                    {
                        headers: {
                            Authorization: `Bearer ${ token }`,
                            "Content-Type": "application/x-www-form-urlencoded"
                        }
                    })
                    .catch(res => {
                        if (res.status === 403) {
                            localStorage.removeItem("wpToken");
                            return this.sendSale(sale);
                        }
                    });
            });
    }

    private _getToken() {
        if (localStorage.getItem("wpToken")) {
            return this.$q.resolve(localStorage.getItem("wpToken"));
        } else {
            const username = localStorage.getItem("wpUsername");
            const password = localStorage.getItem("wpPassword");

            if (username && password) {
                return this._logIn(username, password);
            } else {
                return this.$q.reject("No credentials found in localStorage");
            }
        }
    }

    private _logIn(username, password) {
        return this.$http.post(`${ this.configService.lemag.authUrl }/token`, {
            username: username,
            password: password
        })
            .then(res => {
                const token = res.data && (res.data as any).token;
                if (token) {
                    localStorage.setItem("wpToken", token);
                    return this.$q.resolve(token);
                } else {
                    this.$log.error("Token missing from wordpress response", res);
                    return this.$q.reject();
                }
            });
    }
}
